<script setup>
import { Badge } from '@drama/ui'
import { ExternalLink } from '@lucide/vue'
import { useI18n } from 'vue-i18n'

defineProps({
  resource: { type: Object, required: true },
  rank: { type: Number, default: null },
  similarity: { type: Number, default: null },
  threshold: { type: Number, default: null },
  selected: { type: Boolean, default: false },
})

const { t } = useI18n()

function statusVariant(status) {
  switch (status) {
    case 'published':
      return 'success'
    case 'in_review':
      return 'warning'
    case 'draft':
    case 'rejected':
    case 'archived':
      return 'neutral'
    default:
      return 'neutral'
  }
}

function statusLabel(status) {
  switch (status) {
    case 'draft':
    case 'in_review':
    case 'published':
    case 'rejected':
    case 'archived':
      return t(`visualGameAdmin.llm.runVideoMatch.resourceStatus.${status}`)
    default:
      return status
  }
}
</script>

<template>
  <article class="match-resource" :class="{ 'is-selected': selected }">
    <header v-if="rank !== null">
      <div>
        <strong>{{ t('visualGameAdmin.llm.runVideoMatch.candidateRank', { rank }) }}</strong>
        <Badge v-if="selected" variant="success" size="sm">
          {{ t('visualGameAdmin.llm.videoMatch.selectedCandidate') }}
        </Badge>
      </div>
      <strong>{{ similarity.toFixed(4) }}</strong>
    </header>

    <div class="match-resource__meta">
      <code>{{ resource.id }}</code>
      <Badge :variant="statusVariant(resource.status)" size="sm">
        {{ statusLabel(resource.status) }}
      </Badge>
    </div>
    <p v-if="threshold !== null" class="match-resource__threshold">
      {{ similarity >= threshold
        ? t('visualGameAdmin.llm.runVideoMatch.meetsThreshold', { threshold: threshold.toFixed(4) })
        : t('visualGameAdmin.llm.runVideoMatch.belowThreshold', { threshold: threshold.toFixed(4) }) }}
    </p>
    <p>{{ resource.semantic_text }}</p>
    <video v-if="resource.video_url !== null" :src="resource.video_url" controls preload="metadata" />
    <p v-else class="match-resource__empty">
      {{ t('visualGameAdmin.llm.runVideoMatch.noRenderedVideo') }}
    </p>

    <footer>
      <RouterLink
        :to="{ name: 'intent-video-resource-detail', params: { resourceId: resource.id } }"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ t('visualGameAdmin.llm.runVideoMatch.openResource') }}
        <ExternalLink :size="14" />
      </RouterLink>
      <RouterLink
        :to="{ name: 'llm-calls', query: { capability: 'embedding', request_id: resource.embedding_call_id } }"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ t('visualGameAdmin.llm.runVideoMatch.openResourceEmbedding') }}
        <ExternalLink :size="14" />
      </RouterLink>
      <RouterLink
        v-if="resource.task_request_id !== null"
        :to="{
          name: 'intent-video-logs',
          query: { scenario_id: resource.scenario_id, task: resource.task_request_id },
        }"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ t('visualGameAdmin.llm.runVideoMatch.openTaskLogs') }}
        <ExternalLink :size="14" />
      </RouterLink>
      <span v-else>{{ t('visualGameAdmin.llm.runVideoMatch.noLinkedTask') }}</span>
    </footer>
  </article>
</template>

<style scoped>
.match-resource {
  display: grid;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
}

.match-resource.is-selected {
  border-color: var(--drama-color-success);
}

.match-resource header,
.match-resource header div,
.match-resource__meta,
.match-resource footer,
.match-resource footer a {
  display: flex;
  align-items: center;
  gap: var(--drama-space-xs);
}

.match-resource header,
.match-resource__meta {
  justify-content: space-between;
}

.match-resource p {
  margin: 0;
  line-height: 1.6;
}

.match-resource__threshold,
.match-resource__empty,
.match-resource footer span {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.match-resource code {
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-xs);
  overflow-wrap: anywhere;
}

.match-resource video {
  display: block;
  width: 100%;
  border-radius: var(--drama-radius-md);
}

.match-resource footer {
  flex-wrap: wrap;
  padding-top: var(--drama-space-sm);
  border-top: 1px solid var(--drama-color-border);
}

.match-resource footer a {
  color: var(--drama-color-primary);
  font-size: var(--drama-font-size-sm);
  text-decoration: none;
}
</style>
